/**
 * RFC 9457 오류 응답 파싱 — E10 §1.4 · E13 §7 `DomainExceptionFilter`
 *
 * 생성 클라이언트는 `throwOnError: true`로 호출되므로(react-query.gen.ts), 실패한
 * 요청은 파싱된 오류 본문을 그대로 던진다 — `Error` 인스턴스가 아니라 이 모양의 객체다.
 * `useMutation`·`useQuery`의 `error`는 타입상 `Error`이지만 실제로는 아래 모양이라
 * 화면에서 이 헬퍼로 좁혀 쓴다.
 */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  resolution_url?: string;
  errors?: unknown;
  code?: string;
  /** 덮어쓰기 확인이 필요한 필드/항목 — `AC-ML-04-11` · `AC-ML-04-19` */
  conflicts?: string[];
}

function isProblemDetails(error: unknown): error is ProblemDetails {
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as { status?: unknown }).status === 'number' &&
    typeof (error as { title?: unknown }).title === 'string'
  );
}

/** 사용자에게 보일 한 줄. `detail`이 있으면 그것이 더 구체적이다 (E10 §1.4 Problem Details) */
export function problemMessage(error: unknown, fallback: string): string {
  if (!isProblemDetails(error)) return fallback;
  return error.detail?.trim() || error.title || fallback;
}

export function problemStatus(error: unknown): number | undefined {
  return isProblemDetails(error) ? error.status : undefined;
}

/** `OverwriteConfirmationRequiredError`의 `conflicts` — 덮어쓸 필드/항목 이름 */
export function problemConflicts(error: unknown): string[] | undefined {
  return isProblemDetails(error) ? error.conflicts : undefined;
}
